import { action, computed, observable, makeObservable } from "mobx";
import { fromPromise, type IPromiseBasedObservable, PENDING } from "mobx-utils";

import Api from "nvent-web/services/Api";
import { type Photo } from "nvent-web/types/Photo";

import { RoomsStore } from "./Rooms";

export class PhotosStore {
  newInstallationPhotosPromise?: IPromiseBasedObservable<Photo[]>;
  deleteInstallationPhotoPromise?: IPromiseBasedObservable<Photo[]>;
  newStickerPhotosPromise?: IPromiseBasedObservable<Photo[]>;
  deleteStickerPhotoPromise?: IPromiseBasedObservable<Photo[]>;

  constructor(
    private rooms: RoomsStore,
    private api: Api
  ) {
    makeObservable(this, {
      newInstallationPhotosPromise: observable,
      deleteInstallationPhotoPromise: observable,
      newStickerPhotosPromise: observable,
      deleteStickerPhotoPromise: observable,
      areInstallationPhotosCreating: computed,
      areStickerPhotosCreating: computed,
      createInstallationPhotos: action.bound,
      deleteInstallationPhoto: action.bound,
      createStickerPhotos: action.bound,
      deleteStickerPhoto: action.bound,
    });
  }

  get areInstallationPhotosCreating(): boolean {
    return Boolean(
      this.newInstallationPhotosPromise &&
      this.newInstallationPhotosPromise.state === PENDING
    );
  }

  get areStickerPhotosCreating(): boolean {
    return Boolean(
      this.newStickerPhotosPromise &&
      this.newStickerPhotosPromise.state === PENDING
    );
  }

  async createInstallationPhotos(
    projectId: number,
    roomId: number,
    images: File[]
  ) {
    this.newInstallationPhotosPromise = fromPromise(
      this.api.installationPhotos
        .create(projectId, roomId, images)
        .then(({ data }) => data)
    );

    await this.newInstallationPhotosPromise;
    await this.rooms.getRoom(projectId, roomId);

    return this.newInstallationPhotosPromise;
  }

  async deleteInstallationPhoto(
    projectId: number,
    roomId: number,
    imageId: number
  ) {
    this.deleteInstallationPhotoPromise = fromPromise(
      this.api.installationPhotos
        .delete(projectId, roomId, imageId)
        .then(({ data }) => data)
    );

    await this.deleteInstallationPhotoPromise;
    await this.rooms.getRoom(projectId, roomId);

    return this.deleteInstallationPhotoPromise;
  }

  async createStickerPhotos(projectId: number, roomId: number, images: File[]) {
    this.newStickerPhotosPromise = fromPromise(
      this.api.stickerPhotos
        .create(projectId, roomId, images)
        .then(({ data }) => data)
    );

    await this.newStickerPhotosPromise;
    await this.rooms.getRoom(projectId, roomId);

    return this.newStickerPhotosPromise;
  }

  async deleteStickerPhoto(projectId: number, roomId: number, imageId: number) {
    this.deleteStickerPhotoPromise = fromPromise(
      this.api.stickerPhotos
        .delete(projectId, roomId, imageId)
        .then(({ data }) => data)
    );

    await this.deleteStickerPhotoPromise;
    await this.rooms.getRoom(projectId, roomId);

    return this.deleteStickerPhotoPromise;
  }
}
